"use client"

import { Button, Input } from "antd"
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import PageDrawerMobile, { PageDrawerMobileRef } from "@/components/common/PageDrawerMobile"
import { TypeAirport } from "@/lib/redux/features/flight/type"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import { useAirports } from "@/hooks/flight/useAirports"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { flightActions, matcherTypes } from "@/lib/redux/features/flight/reduce"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlane } from "@fortawesome/free-solid-svg-icons"
import MarkIcon from "@/components/icons/MarkIcon"

export type DrawerSelectPlacementRef = {
  open: () => void
  close: () => void
}

type DrawerSelectPlacementProps = {
  mode: "from" | "to"
  onChange: (airport?: TypeAirport) => void
  onSearch?: (searchValue?: string) => void
  chooseCurrentLy?: (from: TypeAirport, to: TypeAirport) => void
  value?: TypeAirport
  searching?: boolean
}

const DrawerSelectPlacement = forwardRef<DrawerSelectPlacementRef, DrawerSelectPlacementProps>(
  ({ mode, onChange, onSearch, value, searching }, ref) => {
    const { getAirportCityName, getAirportName } = useTETranslation()
    const refPageDrawerMobile = useRef<PageDrawerMobileRef>(null)
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const airportsData = useAirports()
    const domesticAirportCodes = useAppSelector((state) => state.flight.domesticAirports)
    const countryCodes = useAppSelector((state) => state.flight.countryCodes)
    const mappingCountriesAirports = useAppSelector((state) => state.flight.mappingCountriesAirports)
    const searchPopular = useAppSelector((state) => state.flight.searchPopular)

    const [query, setQuery] = useState("")
    const [pending, setPending] = useState<TypeAirport | undefined>(value)
    const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
      setPending(value)
    }, [value])

    useImperativeHandle(ref, () => ({
      open: () => {
        setPending(value)
        setQuery("")
        dispatch(flightActions.searchAirport({ value: "", searchPopular: true }))
        onSearch?.("")
        refPageDrawerMobile.current?.open()
      },
      close: () => refPageDrawerMobile.current?.close(),
    }), [dispatch, onSearch, value])

    const popularAirports = useMemo(() => {
      const preferred = ["SGN", "HAN", "DAD"]
      const codes = preferred.concat(
        domesticAirportCodes.filter((c) => preferred.indexOf(c) < 0),
      )
      return codes.map((code) => airportsData[code]).filter(Boolean) as TypeAirport[]
    }, [airportsData, domesticAirportCodes])

    const searchAirports = useMemo(() => {
      const airportCodes = countryCodes.reduce((arr, countryCode) => {
        return arr.concat(mappingCountriesAirports[countryCode] || [])
      }, [] as string[])

      return matcherTypes.reduce(
        (list, matcherType) =>
          list.sort((a: any, b: any) => {
            if (b?.matchers && typeof b.matchers[matcherType] !== "undefined") return 1
            return -1
          }),
        airportCodes
          .map((code) => airportsData[code])
          .filter(Boolean)
          .sort((a: any, b: any) => (b?.isPopular === "1" ? 1 : -1)),
      ).slice(0, 50) as TypeAirport[]
    }, [airportsData, countryCodes, mappingCountriesAirports])

    const listAirports =
      searching && !searchPopular ? searchAirports : popularAirports

    const handleSearchChange = (val: string) => {
      setQuery(val)
      if (searchTimeout.current) clearTimeout(searchTimeout.current)
      searchTimeout.current = setTimeout(() => {
        dispatch(
          flightActions.searchAirport({
            value: val,
            searchPopular: !val,
          }),
        )
        onSearch?.(val)
      }, 300)
    }

    const handleConfirm = (airport?: TypeAirport) => {
      const next = airport || pending
      if (!next?.code) return
      setPending(next)
      onChange(next)
      refPageDrawerMobile.current?.close()
    }

    const handleCancel = () => {
      setPending(value)
      setQuery("")
      refPageDrawerMobile.current?.close()
    }

    const title = mode === "to" ? t("flight.fly_to") : t("flight.fly_from")

    return (
      <PageDrawerMobile
        ref={refPageDrawerMobile}
        placement="bottom"
        size="90%"
        maskClosable
        hiddenHeader
        className="page__drawer page__drawer--flight-placement"
        styles={{
          body: {
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            padding: 0,
          },
          wrapper: {
            top: "unset",
            bottom: 0,
            boxShadow: "none",
            borderTopLeftRadius: "1.6rem",
            borderTopRightRadius: "1.6rem",
            overflow: "hidden",
          },
          mask: {
            top: 0,
          },
        }}
      >
        <div className="flight-placement-sheet">
          <div className="flight-placement-sheet__handle" aria-hidden />
          <h2 className="flight-placement-sheet__title">{title}</h2>

          <div className="flight-placement-sheet__search">
            <MarkIcon className="flight-placement-sheet__search-icon" aria-hidden />
            <Input
              value={query}
              allowClear
              variant="borderless"
              placeholder={`${t("common.search")}...`}
              className="flight-placement-sheet__search-input"
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          <div className="flight-placement-sheet__list">
            {listAirports.map((airport) => {
              const selected = pending?.code === airport.code
              return (
                <button
                  key={airport.code}
                  type="button"
                  className={`flight-placement-sheet__item${selected ? " flight-placement-sheet__item--selected" : ""}`}
                  onClick={() => handleConfirm(airport)}
                >
                  <span className="flight-placement-sheet__item-icon" aria-hidden>
                    <FontAwesomeIcon icon={faPlane} />
                  </span>
                  <span className="flight-placement-sheet__item-main">
                    <span className="flight-placement-sheet__item-city">
                      {getAirportCityName(airport, { shownCode: false })}
                    </span>
                    <span className="flight-placement-sheet__item-airport">
                      {getAirportName(airport, { shownCode: false })}
                    </span>
                  </span>
                  <span className="flight-placement-sheet__item-code">{airport.code}</span>
                </button>
              )
            })}
          </div>

          <div className="flight-placement-sheet__footer">
            <Button
              type="text"
              size="large"
              className="flight-placement-sheet__cancel"
              onClick={handleCancel}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="primary"
              size="large"
              className="flight-placement-sheet__confirm"
              disabled={!pending?.code}
              onClick={() => handleConfirm()}
            >
              {t("common.confirm")}
            </Button>
          </div>
        </div>
      </PageDrawerMobile>
    )
  },
)

DrawerSelectPlacement.displayName = "DrawerSelectPlacement"

export default DrawerSelectPlacement
